/* eslint-disable max-len */
/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { GlobalState } from '@leverege/ui-redux';
import { Toast, Dialogs, PortalOverlay } from '@leverege/ui-elements';
import { Plugins, Config } from '@leverege/plugin';
import { DataSources, Attributes, AttributeGridEditor } from '@leverege/ui-attributes';
import { ItemDelegate, GroupDelegate } from '@leverege/molten/imagine';
import PairingResponsive from '../views/pairing/PairingResponsive';
import PairingError from '../util/PairingError';
import RecovrSales from '../util/RecovrSales';
import { pairLocatorToVehicle, unpairLocatorFromVehicle } from '../util/Interface';
import S from './PairAction.css';
import C from '../views/salesFlow/Constants';
import Util from '../views/salesFlow/Util';
import { getCompanyAndLocationIdsFromUrl } from '../util/Url';

// eslint-disable-next-line import/prefer-default-export
import { jsx as _jsx } from "react/jsx-runtime";
export function create(opts) {
  const {
    parentNetworkId,
    parentObjectName,
    objectName,
    parentBlueprint,
    networkId,
    blueprint,
    attribute,
    relationship
  } = opts;
  return {
    appearance: props => {
      const {
        context
      } = props;
      const {
        target,
        client
      } = context;
      const vehicleAliasKey = Config.get('VehicleNetwork.dealer', 'aliasKey', 'deviceId');
      const assetId = target?.[0]?.data?.networkAliases?.[parentNetworkId]?.[vehicleAliasKey];
      const trackerId = target?.[0]?.data?.data?.locator?.id;
      const isPendingReplace = target?.[0]?.data?.data?.saleState === 'pendingReplaceLocator';
      const action = trackerId && !isPendingReplace ? 'Unpair' : 'Pair';
      const direction = trackerId && !isPendingReplace ? 'from' : 'to';
      const icon = trackerId && !isPendingReplace ? 'https://recovr-ui-assets.storage.googleapis.com/unpair_action_icon.png' : 'https://recovr-ui-assets.storage.googleapis.com/pair_action_icon.png';
      const name = client === 'DataCardDetail' ? action : `${action} ${objectName} ${direction} ${assetId || parentObjectName}`;
      return {
        name,
        icon,
        imageOpts: {
          style: {
            width: '20px',
            height: '20px'
          }
        }
      };
    },
    perform: props => {
      const context = props?.context?.clientState?.actionsContext || props.context;
      const {
        clientProps,
        target
      } = context;
      const {
        parentItem,
        reloadData,
        delegate
      } = clientProps;
      let [actions] = DataSources.getActions(target);
      if (actions) {
        actions = actions.parent();
      }
      if (!actions) {
        actions = clientProps.actions;
      }
      let location;
      if (parentItem.type === 'location') {
        location = parentItem;
      } else if (parentItem?.data?.data?.location.id) {
        const {
          models
        } = GlobalState.get();
        const {
          lookup
        } = models;
        const item = lookup[parentItem.data.data.location.id];
        location = {
          id: item.id,
          ref: actions.parent().getInterfaceUrl(),
          data: item
        };
      }
      const isPendingReplace = target?.[0]?.data?.data?.saleState === 'pendingReplaceLocator';
      const isPendingRemove = target?.[0]?.data?.data?.saleState === 'pendingRemoveLocator';
      const locatorAliasKey = Config.get('LocatorNetwork', 'aliasKey', 'serialNumber');
      const vehicleAliasKey = Config.get('VehicleNetwork.dealer', 'aliasKey', 'deviceId');
      const asset = target?.[0]?.data;
      const assetId = target?.[0]?.data?.networkAliases?.[parentNetworkId]?.[vehicleAliasKey];
      const tracker = target?.[0]?.data?.data?.locator;
      const trackerId = target?.[0]?.data?.data?.locator?.id;
      const trackerNetworkId = target?.[0]?.data?.data?.locator?.data?.[locatorAliasKey];
      const trackerInstallLocation = target?.[0]?.data?.data?.locator?.data?.installLocation;
      async function onSubmit({
        data: props,
        value
      }) {
        try {
          const res = await GlobalState.dispatch(actions.create(value));
          Toast.success(`${parentObjectName} created`);
          return res;
        } catch (err) {
          Toast.error([`Failed to Create ${parentObjectName}`, err.message]);
          return null;
        }
      }
      const factory = Plugins.createFactory('AttributeValueEditorForm', {
        strategy: Plugins.reactStrategy(React, 'type'),
        pluginKey: 'form',
        defaultObject: AttributeGridEditor
      });
      const attributes = getAttributes(parentBlueprint.type, 'create');
      function AddDeviceForm(addProps) {
        const {
          onCancel,
          target,
          onSuccess
        } = addProps;
        const isMobile = useMediaQuery({
          query: '(max-width: 430px)'
        });
        const CreateComponent = factory.create(`${parentBlueprint.type}.create`, {
          target: {
            ...target,
            ref: delegate.getInterfaceUrl()
          },
          title: `Create ${parentObjectName}`,
          attributes,
          submitText: `Create ${parentObjectName}`,
          eventData: props,
          icon: parentBlueprint.getIcon('createDialogIcon') || parentBlueprint.getIcon('screenIcon'),
          pairing: true,
          context: {
            clientProps: {
              pairing: true,
              ...props
            },
            usage: 'form',
            isCreate: true
          },
          onCancel,
          onSuccess
        });
        return /*#__PURE__*/_jsx(PortalOverlay, {
          position: isMobile ? 'full' : 'center',
          show: true,
          closeOnEscape: false,
          onClose: onCancel,
          closeOnDocumentClick: false,
          children: /*#__PURE__*/_jsx("div", {
            className: S.container,
            children: CreateComponent
          })
        });
      }
      const onPair = async data => {
        const {
          assetId,
          trackerId,
          trackerInstallLocation,
          locatorFound
        } = data;
        let asset, tracker;
        if (assetId) {
          try {
            asset = (await GlobalState.dispatch(actions.search({
              limit: 100,
              filter: {
                type: 'logical',
                operator: 'and',
                conditions: [{
                  type: 'equals',
                  field: `networkAliases/${parentNetworkId}/${vehicleAliasKey}`,
                  fieldType: 'string',
                  value: [assetId]
                }]
              }
            }, null, 'pairActionSearch')))?.items?.[0];
          } catch (error) {
            console.log(error);
            Sentry.captureException(error);
          }
        }
        if (trackerId) {
          try {
            tracker = (await GlobalState.dispatch(actions.parent().child('locators').search({
              limit: 100,
              filter: {
                type: 'logical',
                operator: 'and',
                conditions: [{
                  type: 'equals',
                  field: `networkAliases/${networkId}/${locatorAliasKey}`,
                  fieldType: 'string',
                  value: [trackerId]
                }]
              }
            }, null, 'pairActionSearch')))?.items?.[0];
          } catch (error) {
            console.log(error);
            Sentry.captureException(error);
          }
        }
        const vehicle = asset;
        const locator = tracker;
        if (vehicle?.data?.saleState === 'pendingReplaceLocator' && Util.isLocatorLowBattery(locator)) {
          throw new PairingError({
            status: 500,
            customType: 'lowBatteryPending',
            message: 'Can not pair a locator with less than 70% battery to a vehicle pending sale',
            asset,
            tracker
          });
        }
        if (!Util.isLocatorActivated(locator)) {
          throw new PairingError({
            status: 500,
            customType: 'unactivated',
            message: 'You must activate a locator to pair it to a vehicle',
            asset,
            tracker
          });
        }
        if (Util.isLocatorOffline(locator)) {
          throw new PairingError({
            status: 500,
            customType: 'locatorOffline',
            message: 'This locator is offline and cannot be paired',
            asset,
            tracker
          });
        }
        if (Util.isGpsInoperative(locator)) {
          throw new PairingError({
            status: 500,
            customType: 'gpsInoperative',
            message: 'This locator is not functioning properly and should be returned to RecovR.  Please ship to RMA or contact support.',
            asset,
            tracker
          });
        }
        try {
          const itemActions = actions.item(asset.id);
          const interfaceUrl = actions.parent().getInterfaceUrl();
          const {
            companyId,
            locationId
          } = getCompanyAndLocationIdsFromUrl(interfaceUrl);
          const saleId = locatorFound ? locatorFound.saleId : null;
          await pairLocatorToVehicle({
            locatorId: locator.id,
            vehicleId: vehicle.id,
            companyId,
            locationId,
            installLocation: trackerInstallLocation,
            force: true,
            saleId
          });
          try {
            // This may fail if we have moved the vehicle to a new location.
            // If so, we don't really care.
            await GlobalState.dispatch(itemActions.get());
          } catch (err) {
            console.error(err);
          }
          return {
            asset,
            tracker
          };
        } catch (err) {
          if (err instanceof PairingError) {
            throw err;
          }
          throw new PairingError({
            status: err.status,
            message: `Failed to pair ${trackerId} to ${assetId}. Please try again.`,
            asset,
            tracker
          });
        }
      };
      const onUnpair = async data => {
        const {
          trackerId,
          unpairReason,
          customerName,
          salesperson,
          createdBy,
          // vehicle-only, vehicle-wholesale, dealer-trade
          notes // locator-issue, other
        } = data;
        let tracker;
        if (trackerId) {
          try {
            tracker = (await GlobalState.dispatch(actions.parent().child('locators').search({
              limit: 100,
              filter: {
                type: 'logical',
                operator: 'and',
                conditions: [{
                  type: 'equals',
                  field: `networkAliases/${networkId}/${locatorAliasKey}`,
                  fieldType: 'string',
                  value: [trackerId]
                }]
              }
            }, null, 'pairActionSearch')))?.items?.[0];
          } catch (error) {
            console.log(error);
            Sentry.captureException(error);
          }
        }
        const trackerData = tracker;
        const vehicleId = trackerData?.data?.vehicle?.id;
        if (!vehicleId) {
          throw new PairingError({
            status: 404,
            message: `Failed to unpair ${trackerId}. The tracker is not currently paired.`,
            tracker
          });
        }
        try {
          const vehicleActions = actions.item(vehicleId);
          let vehicle = target?.[0];
          try {
            const vData = await GlobalState.dispatch(vehicleActions.get());
            vehicle = {
              data: vData
            };
          } catch (err) {
            console.warn({
              err
            }, 'Unable to get vehicle data');
          }
          if (unpairReason === C.SALE_TYPES.VEHICLE_ONLY || unpairReason === C.SALE_TYPES.VEHICLE_WHOLESALE || unpairReason === C.SALE_TYPES.DEALER_TRADE || unpairReason === C.SALE_TYPES.VEHICLE_WITH_LOCATOR) {
            if (!vehicle?.data?.data?.pendingSaleId) {
              const res = await RecovrSales.sell({
                actions: vehicleActions,
                vehicle: {
                  id: vehicleId
                },
                newLocator: {
                  id: trackerData.id
                },
                vehicleLeftLot: false,
                saleType: unpairReason,
                hasCustomer: false,
                customerName,
                salesperson,
                createdBy,
                goToSales: delegate instanceof ItemDelegate,
                throwErr: true,
                unpair: true
              });
              if (delegate instanceof GroupDelegate) {
                reloadData?.();
              }
              return {
                tracker: trackerData,
                saleId: res.saleId
              };
            }
          }
          const interfaceUrl = actions.parent().getInterfaceUrl();
          const {
            companyId,
            locationId
          } = getCompanyAndLocationIdsFromUrl(interfaceUrl);
          await unpairLocatorFromVehicle({
            locatorId: tracker.id,
            companyId,
            locationId,
            unpairReason,
            notes
          });
          if (unpairReason !== C.SALE_TYPES.VEHICLE_ONLY && unpairReason !== C.SALE_TYPES.VEHICLE_WHOLESALE && unpairReason !== C.SALE_TYPES.DEALER_TRADE && unpairReason !== C.SALE_TYPES.VEHICLE_WITH_LOCATOR) {
            await GlobalState.dispatch(vehicleActions.get());
          }
          return {
            tracker: trackerData
          };
        } catch (err) {
          throw new PairingError({
            status: err.status,
            message: `Failed to unpair ${trackerId}. Please try again.`,
            tracker
          });
        }
      };
      Dialogs.show({
        component: PairingResponsive,
        props: {
          networkId,
          parentItem,
          parentNetworkId,
          actions,
          blueprint,
          attribute,
          relationship,
          AddDeviceForm,
          onSubmit,
          onPair,
          onUnpair,
          pairTitleIcon: 'https://recovr-ui-assets.storage.googleapis.com/pair_title_icon.png',
          unpairTitleIcon: 'https://recovr-ui-assets.storage.googleapis.com/unpair_title_icon.png',
          titleVariant: 'dialogTitle',
          asset,
          assetId,
          tracker,
          trackerId: trackerNetworkId,
          trackerDeviceId: trackerId,
          trackerInstallLocation,
          isPendingReplace,
          isPendingRemove,
          location
        }
      });
    }
  };
}
function getAttributes(type, action = 'create', dedupeUpdatePath = true) {
  const attrs = Attributes.getAttributesFor(type).sort(attributeSort);
  if (!dedupeUpdatePath) {
    return attrs;
  }
  const updatePaths = {};
  return attrs.filter(attr => {
    if (attr.source?.attribute?.hasTag?.(`${action}:exclude`)) {
      return false;
    }
    const updatePath = attr.source?.updatePath;
    if (!updatePath) {
      return true;
    }
    if (updatePath && updatePaths[updatePath]) {
      return false;
    }
    updatePaths[updatePath] = true;
    return true;
  });
}
function attributeSort(a, b) {
  const as = a.source?.attribute?.getTagStartingWith?.('formSort:', true) || 'zzz';
  const bs = b.source?.attribute?.getTagStartingWith?.('formSort:', true) || 'zzz';
  return as.localeCompare(bs);
}